import { useState } from 'react';
import { BellPlus, Save, X } from 'lucide-react';
import { toast } from 'sonner';

export type ReminderDraft = {
  id: string;
  date: string;
  note: string;
  done?: boolean;
};

interface Props {
  initial?: ReminderDraft;
  onClose: () => void;
  onSave: (r: ReminderDraft) => void;
  /** Sous-titre contextuel (ex: "Facture F-2024-0123 — Client X"). */
  subtitle?: string;
}

/**
 * Modal partagé pour créer/éditer un rappel personnalisé.
 * Utilisé par Relances Client ET Relances Factures pour garantir
 * la même UX sur les deux pages.
 */
export default function ReminderModal({ initial, onClose, onSave, subtitle }: Props) {
  const [date, setDate] = useState(initial?.date || new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
  const [note, setNote] = useState(initial?.note || '');
  const submit = () => {
    if (!date) { toast.error('Date requise'); return; }
    onSave({ id: initial?.id || crypto.randomUUID(), date, note: note.trim(), done: initial?.done });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-100 flex items-center gap-2">
            <BellPlus size={18} strokeWidth={1.5} className="text-violet-300" />
            {initial ? 'Modifier le rappel' : 'Nouveau rappel personnalisé'}
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-200">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        {subtitle && <div className="text-xs text-slate-400 mb-3">{subtitle}</div>}
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Date du rappel</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-slate-950 border border-slate-700 text-sm text-slate-200 focus:outline-none focus:border-violet-500/50" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Note (motif, contexte…)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              placeholder="Ex.: client en pause projet, à rappeler après les vacances"
              className="w-full px-3 py-2 rounded-md bg-slate-950 border border-slate-700 text-sm text-slate-200 focus:outline-none focus:border-violet-500/50" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md text-xs border border-slate-700 text-slate-300 hover:bg-slate-800">Annuler</button>
          <button onClick={submit} className="px-3 py-1.5 rounded-md text-xs border bg-violet-500/20 border-violet-500/40 text-violet-200 hover:bg-violet-500/30 flex items-center gap-1.5">
            <Save size={12} strokeWidth={1.5} /> {initial ? 'Enregistrer' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
}
